import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { userSelector } from 'Redux/LoggedUserSlice';
import useTranslation from 'Hooks/useTranslation';
import { getDashboardCourses, DashboardCoursesDataI } from 'Api/dashboard-api';
import { getNewsFeed } from 'Api/news-api';
import { s3 } from 'Services/AWS/s3';
import mapCourseToRouteState from 'Utils/map-course-to-route-state';
import stripHtml from 'Utils/strip-html';
import type { NewsFeedItemI } from 'types';
import type { StatusVariant } from 'Components/StatusPill/StatusPill.styled';
import { formatDueDate, formatNewsDate } from 'Utils/format-locale-dates';

export const MY_COURSES_PATH = '/mis-cursos';

export interface PendingCourseVM {
  id: string;
  name: string;
  thumbnailUrl: string | null;
  modulesText: React.ReactNode;
  dueText: React.ReactNode;
}

export interface NextCourseVM {
  id: string;
  name: string;
  moduleCount: number;
  dueDate: string | null;
}

export interface ApprovedRowVM {
  id: string;
  name: string;
  statusLabel: string;
  statusVariant: StatusVariant;
}

export interface NewsVM {
  id: string;
  tag: string;
  title: string;
  /** Texto plano (markdown despojado) para el excerpt del rail. */
  excerpt: string;
  /** Descripción completa en markdown crudo; el modal la renderiza. */
  description: string;
  dateLabel: string;
  /** URL S3 ya firmada (o null → el rail pinta su placeholder). */
  imageUrl: string | null;
}

export default function useDashboardPage() {
  const { t2, language } = useTranslation('Dashboard');
  const { fullName } = useSelector(userSelector);
  const history = useHistory();

  const [coursesData, setCoursesData] = useState<DashboardCoursesDataI | null>(null);
  const [news, setNews] = useState<NewsFeedItemI[]>([]);
  const [newsImageUrls, setNewsImageUrls] = useState<Record<string, string | null>>({});
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState<NewsVM | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'auto';
  }, []);

  useEffect(() => {
    let isMounted = true;
    setCoursesData(null);
    setError(null);

    Promise.all([getDashboardCourses(), getNewsFeed({ page: 1, limit: 20 })]).then(
      ([coursesResponse, newsResponse]) => {
        if (!isMounted) return;
        if (coursesResponse.error !== null || coursesResponse.data === null) {
          setError(coursesResponse.error ?? 'FETCH_ERROR');
          return;
        }
        setCoursesData(coursesResponse.data);
        // Un fallo del feed NO tumba el dashboard: el rail cae a EmptyNews.
        setNews(newsResponse.error === null && newsResponse.data !== null ? newsResponse.data.items : []);
      }
    );

    return () => {
      isMounted = false;
    };
  }, [reloadToken]);

  // Firmado en lote: la key S3 cruda se cambia por URL firmada aquí, nunca en el rail.
  useEffect(() => {
    let isMounted = true;
    const withKey = news.filter(item => item.imageKey);
    if (withKey.length === 0) {
      setNewsImageUrls({});
      return undefined;
    }

    Promise.all(
      withKey.map(item =>
        s3
          .getSignedUrl(item.imageKey as string)
          .then((response: { signedUrl?: string } | null) => [item.id, response?.signedUrl ?? null] as const)
          .catch(() => [item.id, null] as const)
      )
    ).then(entries => {
      if (isMounted) setNewsImageUrls(Object.fromEntries(entries));
    });

    return () => {
      isMounted = false;
    };
  }, [news]);

  const loading = coursesData === null && error === null;
  const retry = () => setReloadToken(token => token + 1);

  const courses = coursesData?.courses ?? [];
  const summary = coursesData?.summary ?? { pending: 0, approved: 0, failed: 0, total: 0 };

  const pending = useMemo(() => courses.filter(course => course.status === 'pending'), [coursesData]);

  const pendingCourses: PendingCourseVM[] = useMemo(
    () =>
      pending.map(course => ({
        id: course.id,
        name: course.title,
        thumbnailUrl: course.thumbnail,
        modulesText: t2('Modules', [String(course.modulesCount)]),
        dueText: course.expirationDate ? t2('DueLabel', [formatDueDate(course.expirationDate, language)]) : null,
      })),
    [pending, language]
  );

  // Siguiente paso: la asignación pendiente más reciente (orden del backend)
  const nextCourse: NextCourseVM | null = useMemo(() => {
    const top = pending[0];
    if (!top) return null;
    return {
      id: top.id,
      name: top.title,
      moduleCount: top.modulesCount,
      dueDate: top.expirationDate ? formatDueDate(top.expirationDate, language) : null,
    };
  }, [pending, language]);

  const approvedRows: ApprovedRowVM[] = useMemo(
    () =>
      courses
        .filter(course => course.status === 'approved')
        .map(course => ({
          id: course.id,
          name: course.title,
          statusLabel: t2('StatusCompleted') as string,
          statusVariant: 'ok' as StatusVariant,
        })),
    [coursesData, language]
  );

  const { newsHero, newsItems } = useMemo(() => {
    // El back ya ordena pinned DESC, publishAt DESC: el hero es el primero, sin reordenar.
    const hero = news[0] ?? null;
    const toVM = (item: NewsFeedItemI): NewsVM => ({
      id: item.id,
      tag: item.pinned ? (t2('Pinned') as string) : formatNewsDate(item.publishAt, language),
      title: item.title,
      excerpt: stripHtml(item.description),
      description: item.description,
      dateLabel: formatNewsDate(item.publishAt, language),
      imageUrl: newsImageUrls[item.id] ?? null,
    });
    return {
      newsHero: hero ? toVM(hero) : null,
      newsItems: news.slice(1).map(toVM),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [news, newsImageUrls, language]);

  const onViewCourse = (id: string) => {
    const course = courses.find(item => item.id === id);
    if (!course) return;
    history.push({
      pathname: '/cursos/detalles',
      state: {
        update: true,
        course: mapCourseToRouteState(course),
        isComingFrom: '/dashboard',
      },
    });
  };

  const onResumeCourse = () => {
    if (nextCourse) onViewCourse(nextCourse.id);
  };

  const openNews = (id: string) => {
    const allNews = newsHero ? [newsHero, ...newsItems] : newsItems;
    const selected = allNews.find(item => item.id === id) ?? null;
    if (selected) setSelectedNews(selected);
  };

  const userName = fullName || (t2('User') as string);
  const assignedCount = summary.total;
  const pendingCount = summary.pending;

  return {
    t2,
    userName,
    loading,
    error,
    retry,
    assignedCount,
    pendingCount,
    approvedCount: summary.approved,
    failedCount: summary.failed,
    nextCourse,
    pendingCourses,
    approvedRows,
    newsHero,
    newsItems,
    helpOpen,
    openHelp: () => setHelpOpen(true),
    closeHelp: () => setHelpOpen(false),
    selectedNews,
    openNews,
    closeNews: () => setSelectedNews(null),
    onResumeCourse,
    onViewCourse,
  };
}
